/// <amd-module name="VSSUI/Components/Toggle/Toggle" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext, IFocusZoneContext } from "VSSUI/FocusZone";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { getTabIndex, IFocusable } from "VSSUI/Utilities/Focus";

import { IToggleProps } from "./Toggle.Props";

let toggleId = 1;

export class Toggle extends React.Component<IToggleProps> implements IFocusable<{}> {
    public static contextType = FocusGroupContext;

    private toggleElement = React.createRef<HTMLDivElement>();
    private labelId = `toggle-${toggleId++}-label`;

    public render(): JSX.Element {
        const { onAriaLabel, offAriaLabel, className, disabled, excludeFocusZone, id, offText, onText, role, text, tooltipProps } = this.props;

        return (
            <FocusZoneContext.Consumer>
                {(zoneContext: IFocusZoneContext) => (
                    <Observer checked={this.props.checked}>
                        {(props: { checked: boolean | undefined }) => {
                            const ariaLabel = props.checked ? onAriaLabel || this.props.ariaLabel : offAriaLabel || this.props.ariaLabel;
                            const labelId = getSafeId(this.labelId);
                            let toggle = (
                                <div
                                    className={css(
                                        className,
                                        "bolt-toggle-button cursor-pointer",
                                        props.checked && "checked",
                                        disabled ? "disabled" : "enabled"
                                    )}
                                    onClick={this.onClick}
                                    onFocus={this.onFocus}
                                    onKeyDown={this.onKeyDown}
                                >
                                    <div
                                        aria-checked={props.checked}
                                        aria-disabled={disabled}
                                        aria-label={ariaLabel}
                                        aria-labelledby={!ariaLabel ? labelId : undefined}
                                        className="bolt-toggle-button-pill bolt-focus-treatment flex-noshrink"
                                        data-focuszone={!disabled && !excludeFocusZone ? zoneContext.focuszoneId : undefined}
                                        data-is-focusable={true}
                                        id={getSafeId(id)}
                                        ref={this.toggleElement}
                                        role={role || "switch"}
                                        tabIndex={getTabIndex(this.props, this.context)}
                                    >
                                        <div className="bolt-toggle-button-icon" />
                                    </div>
                                    <div className="bolt-toggle-button-text body-m" id={labelId}>
                                        {props.checked ? onText || text : offText || text}
                                    </div>
                                </div>
                            );

                            if (tooltipProps) {
                                toggle = <Tooltip {...tooltipProps}>{toggle}</Tooltip>;
                            }

                            return toggle;
                        }}
                    </Observer>
                )}
            </FocusZoneContext.Consumer>
        );
    }

    public focus(): void {
        if (this.toggleElement.current) {
            this.toggleElement.current.focus();
        }
    }

    private onClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (this.props.onChange && !this.props.disabled) {
            this.props.onChange(event, this.props.checked === undefined ? false : !ObservableLike.getValue(this.props.checked));
        }
    };

    private onFocus = () => {
        if (this.props.id) {
            this.context.onFocus(this.props.id);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.space) {
                this.onClick(event);
                event.preventDefault();
            }
        }
    };
}
